import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Megaphone, Info, CheckCircle2, AlertTriangle, OctagonAlert, X, ExternalLink } from 'lucide-react';
import { ApiClient } from '../../api/client';
import type { AnnouncementLevel, SystemAnnouncement } from '../../types';

// ═══════════════════════════════════════════════════════════════
// Rendszerüzenetek megjelenítése
//   • AnnouncementBar – belépés után, a felső sáv alatt: fényújság (futó
//     szöveg) és/vagy rögzített rendszerüzenet-sáv
//   • LoginAnnouncements – a központi és a szervezeti belépési oldalon
// Az üzenet sima szövegként jelenik meg (React escape), HTML-t nem értelmez.
// ═══════════════════════════════════════════════════════════════

const LEVEL_STYLE: Record<AnnouncementLevel, { bar: string; chip: string; card: string; icon: React.ElementType; label: string }> = {
  info: {
    bar: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-950/60 dark:text-sky-100 dark:border-sky-900',
    chip: 'bg-sky-600 text-white',
    card: 'bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-950/50 dark:border-sky-800 dark:text-sky-100',
    icon: Info, label: 'Tájékoztató',
  },
  success: {
    bar: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-100 dark:border-emerald-900',
    chip: 'bg-emerald-600 text-white',
    card: 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/50 dark:border-emerald-800 dark:text-emerald-100',
    icon: CheckCircle2, label: 'Hír',
  },
  warning: {
    bar: 'bg-amber-50 text-amber-950 border-amber-200 dark:bg-amber-950/60 dark:text-amber-100 dark:border-amber-900',
    chip: 'bg-amber-500 text-amber-950',
    card: 'bg-amber-50 border-amber-300 text-amber-950 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-100',
    icon: AlertTriangle, label: 'Figyelem',
  },
  critical: {
    bar: 'bg-rose-600 text-white border-rose-700',
    chip: 'bg-white text-rose-700',
    card: 'bg-rose-50 border-rose-300 text-rose-950 dark:bg-rose-950/60 dark:border-rose-800 dark:text-rose-100',
    icon: OctagonAlert, label: 'Fontos',
  },
};
const LEVEL_RANK: Record<AnnouncementLevel, number> = { info: 0, success: 1, warning: 2, critical: 3 };

export const announcementLevelStyle = LEVEL_STYLE;

// ── Elrejtett üzenetek (böngészőnként; módosított üzenet újra megjelenik) ──
const DISMISS_KEY = 'kf_dismissed_announcements';
const dismissKey = (a: SystemAnnouncement) => `${a.id}@${a.updated_at}`;
function readDismissed(): string[] {
  try { const v = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
function writeDismissed(list: string[]) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(list.slice(-100))); } catch { /* ignore */ }
}

const safeHref = (u: string | null) => (u && /^https?:\/\//i.test(u) ? u : null);

const LinkOut: React.FC<{ a: SystemAnnouncement; className?: string }> = ({ a, className }) => {
  const href = safeHref(a.link_url);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 font-semibold underline underline-offset-2 ${className || ''}`}>
      {a.link_label || 'Részletek'} <ExternalLink className="w-3 h-3" />
    </a>
  );
};

// ── Fényújság ───────────────────────────────────────────────
export const TickerStrip: React.FC<{ items: SystemAnnouncement[]; onClose?: () => void }> = ({ items, onClose }) => {
  const top = items.reduce<AnnouncementLevel>((m, a) => (LEVEL_RANK[a.level] > LEVEL_RANK[m] ? a.level : m), 'info');
  const st = LEVEL_STYLE[top];
  const Icon = st.icon;
  const textLen = items.reduce((n, a) => n + a.message.length + (a.title?.length || 0), 0);
  const duration = Math.min(90, Math.max(18, Math.round(textLen / 6)));
  const content = (
    <>
      {items.map((a) => (
        <span key={a.id} className="inline-flex items-center gap-2 mr-16">
          {a.title && <b>{a.title}:</b>}
          <span>{a.message}</span>
          <LinkOut a={a} />
        </span>
      ))}
    </>
  );
  return (
    <div role="status" aria-live="polite" className={`kf-ticker relative flex items-center w-full max-w-full min-w-0 h-8 border-b text-xs overflow-hidden shrink-0 ${st.bar}`}>
      <style>{`
        @keyframes kf-ticker-move { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .kf-ticker-track { animation: kf-ticker-move var(--kf-dur) linear infinite; }
        .kf-ticker:hover .kf-ticker-track, .kf-ticker:focus-within .kf-ticker-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .kf-ticker-track { animation: none; } }
      `}</style>
      <span className={`flex items-center gap-1.5 h-full px-2.5 font-bold uppercase tracking-wide text-[10px] shrink-0 z-10 ${st.chip}`}>
        <Megaphone className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{st.label}</span>
      </span>
      {/* A futó szöveg abszolút pozícióban van, így a hossza semmilyen böngészőben
          nem szélesítheti a környező elrendezést (gépelés közbeni „tolódás” ellen). */}
      <div className="relative flex-1 min-w-0 overflow-hidden h-full" title="Az egeret fölé víve megáll">
        <div className="kf-ticker-track absolute left-0 top-0 h-full flex items-center whitespace-nowrap pl-4 w-max" style={{ ['--kf-dur' as any]: `${duration}s` }}>
          <span className="inline-flex">{content}</span>
          <span className="inline-flex" aria-hidden="true">{content}</span>
        </div>
      </div>
      <Icon className="w-3.5 h-3.5 mx-2 shrink-0 opacity-70" aria-hidden="true" />
      {onClose && (
        <button onClick={onClose} className="px-2 h-full shrink-0 opacity-70 hover:opacity-100 cursor-pointer" aria-label="Fényújság elrejtése" title="Elrejtés">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// ── Rögzített rendszerüzenet-sáv ────────────────────────────
export const BannerStrip: React.FC<{ a: SystemAnnouncement; onClose?: () => void }> = ({ a, onClose }) => {
  const st = LEVEL_STYLE[a.level];
  const Icon = st.icon;
  return (
    <div role={a.level === 'critical' ? 'alert' : 'status'} className={`flex items-start gap-2.5 px-3 sm:px-4 py-2 border-b text-xs sm:text-[13px] shrink-0 ${st.bar}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0 leading-snug [overflow-wrap:anywhere]">
        {a.title && <b className="mr-1.5">{a.title}</b>}
        <span className="whitespace-pre-line">{a.message}</span>
        {safeHref(a.link_url) && <span className="ml-2"><LinkOut a={a} /></span>}
      </div>
      {onClose && (
        <button onClick={onClose} className="p-0.5 shrink-0 opacity-70 hover:opacity-100 cursor-pointer" aria-label="Üzenet elrejtése" title="Elrejtés">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ── Belépés után: a felső sáv alatt ──────────────────────────
export const AnnouncementBar: React.FC<{ tenantKey?: string | number | null }> = ({ tenantKey }) => {
  const [items, setItems] = useState<SystemAnnouncement[]>([]);
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed());

  const load = useCallback(async () => {
    try {
      const r = await ApiClient.getActiveAnnouncements();
      if (r.ok && Array.isArray(r.data)) setItems(r.data);
    } catch { /* a rendszerüzenet nem kritikus */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 120_000);
    const onChange = () => load();
    window.addEventListener('kozad:announcements-changed', onChange);
    return () => { clearInterval(t); window.removeEventListener('kozad:announcements-changed', onChange); };
  }, [load, tenantKey]);

  const visible = useMemo(() => items.filter((a) => !(a.dismissible && dismissed.includes(dismissKey(a)))), [items, dismissed]);
  const tickers = visible.filter((a) => a.app_style === 'ticker');
  const banners = visible.filter((a) => a.app_style === 'banner');
  if (!visible.length) return null;

  const dismiss = (list: SystemAnnouncement[]) => {
    const next = [...dismissed, ...list.filter((a) => a.dismissible).map(dismissKey)];
    setDismissed(next); writeDismissed(next);
  };

  return (
    <>
      {banners.map((a) => <BannerStrip key={a.id} a={a} onClose={a.dismissible ? () => dismiss([a]) : undefined} />)}
      {tickers.length > 0 && (
        <TickerStrip items={tickers} onClose={tickers.every((a) => a.dismissible) ? () => dismiss(tickers) : undefined} />
      )}
    </>
  );
};

// ── Belépési oldal ──────────────────────────────────────────
export const AnnouncementCard: React.FC<{ a: SystemAnnouncement }> = ({ a }) => {
  const st = LEVEL_STYLE[a.level];
  const Icon = st.icon;
  return (
    <div role={a.level === 'critical' ? 'alert' : 'status'} className={`flex gap-2.5 p-3 rounded-xl border text-xs leading-relaxed ${st.card}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 [overflow-wrap:anywhere]">
        {a.title && <div className="font-bold text-[13px] mb-0.5">{a.title}</div>}
        <div className="whitespace-pre-line">{a.message}</div>
        {safeHref(a.link_url) && <div className="mt-1"><LinkOut a={a} /></div>}
      </div>
    </div>
  );
};

export const LoginAnnouncements: React.FC<{ loginToken?: string | null; className?: string }> = ({ loginToken, className }) => {
  const [items, setItems] = useState<SystemAnnouncement[]>([]);
  useEffect(() => {
    let cancelled = false;
    ApiClient.getLoginAnnouncements(loginToken)
      .then((r) => { if (!cancelled && r.ok && Array.isArray(r.data)) setItems(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loginToken]);
  if (!items.length) return null;
  return (
    <div className={`space-y-2 ${className || ''}`}>
      {items.map((a) => <AnnouncementCard key={a.id} a={a} />)}
    </div>
  );
};
